import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy, ArrowLeft, RefreshCw } from "lucide-react";
import Report from "@/components/Report";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportPage() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const loadReport = useCallback(async () => {
    const cached = sessionStorage.getItem(`velo21:${id}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setReport(parsed);
        setLoading(false);
        return;
      } catch (parseError) {
        console.warn("ReportPage: cached report parse failed", parseError);
        // fall through to network fetch
      }
    }
    try {
      const { data } = await axios.get(`${API}/report/${id}`);
      setReport(data);
    } catch (fetchError) {
      console.error("ReportPage: failed to load report", fetchError);
      const detail = fetchError?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Report not found.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied — send it to a co-founder.");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="report-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-[#FF4757]" />
      </div>
    );
  }
  if (err || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="report-error">
        <div className="velo-card p-10 text-center max-w-md">
          <h2 className="font-serif-display text-3xl mb-3">Report not found.</h2>
          <p className="text-zinc-600 text-sm mb-6">{err || "It may have been removed."}</p>
          <Link to="/analyze" className="btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-block">
            Run a new analysis
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-bg" data-testid="report-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-zinc-200/60">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="report-home-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-bold tracking-tight">velo2one</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={copyLink}
              className="rounded-full px-4 py-2 text-xs font-semibold border border-zinc-300 hover:border-zinc-900 transition-colors inline-flex items-center gap-2"
              data-testid="copy-link-btn"
            >
              <Copy className="w-3.5 h-3.5" /> Share
            </button>
            <Link
              to="/analyze"
              className="btn-primary rounded-full px-4 py-2 text-xs font-semibold"
              data-testid="new-analysis-btn"
            >
              New analysis
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <Report report={report} />
      </main>
    </div>
  );
}
